import { FileOpener } from '@capacitor-community/file-opener'
import { App } from '@capacitor/app'
import { Clipboard } from '@capacitor/clipboard'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { InAppBrowser } from '@capacitor/inappbrowser'
import { Preferences } from '@capacitor/preferences'
import {
    APPLINKS_DEMO_URL,
    APPLINKS_DEV_URL,
    APPLINKS_TEST_URL,
    AppUser,
    CountryCodesWithNames,
    DEMO_SUBDOMAIN,
    DEV_SUBDOMAIN,
    EventrDate,
    ROCKETCHAT_DEMO_DOMAIN,
    ROCKETCHAT_DEV_DOMAIN,
    ROCKETCHAT_TEST_DOMAIN,
    TEST_SUBDOMAIN,
    TOrgRole,
    UserOrganisation,
    V2,
    checkClaimsWeb,
    setDevicePref,
    spError,
    spErrorTags,
} from '@eventr/common'
import * as Sentry from '@sentry/capacitor'
import { compareVersions } from 'compare-versions'
import { LngLat } from 'maplibre-gl'
import { deviceDetect } from 'react-device-detect'
import { ISection } from './data/Data'
import { isConnectivityAvailable } from './feat/connectivity/connectivityListener'
import { FlutterLog, logsSignal } from './feat/developerMenu/logs/logsPage'
import { useGuestStore } from './feat/guest/guest.store'
import i18n from './i18n/i18next'
import { bs } from './state/bound.store'
import { IItinerary } from './state/interfaces'
import { defaultAddSnack } from './state/overlayState/overlayContext'
import { loadingSignal } from './state/overlayState/overlaySignals'
import { useThemeContext } from './state/themeState/themeContext'
import { reactAppStage } from './utils/reactAppStage'
import { addOneDayIfDateType } from './v2/v2Utils'

export const ID_PREFIX = '!##!'

export const getMobileOperatingSystem = () => {
    var userAgent = navigator.userAgent

    if (/android/i.test(userAgent)) {
        return 'Android'
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent)) {
        return 'iOS'
    }

    return 'unknown'
}

export const compareBuildVersion = (
    currentBuildVersion: string,
    versionLock: string
) => {
    try {
        if (Capacitor.isNativePlatform() && currentBuildVersion) {
            // spLogTags(
            //     `Version: ${currentBuildVersion ?? 'undefined'} Version Lock: ${
            //         versionLock ?? 'undefined'
            //     }`,
            //     ['BuildVersionCompare']
            // )
            return (
                compareVersions(
                    currentBuildVersion,
                    !versionLock ? '100.0.0' : versionLock
                ) !== -1
            )
        } else if (!currentBuildVersion) {
            return false
        } else {
            return true
        }
    } catch {
        console.error('Build Version feature is currently not available')
    }
}

export const fixedUrl = (url = '') => {
    var fixedUrl = url
    if (url.startsWith('www')) {
        fixedUrl = 'https://' + url
    }
    return fixedUrl
}

export const getUserName = (firstName: any, lastName: any) => {
    const firstNameUpper =
        firstName.charAt(0).toUpperCase() + firstName.slice(1)
    const lastNameUpper =
        lastName?.charAt(0).toUpperCase() ?? '' + lastName?.slice(1) ?? ''
    return `${firstNameUpper + ' ' + lastNameUpper}`
}

export const getNextFlightData = (data: any) => {
    const flightData = data?.v2?.find((d: any) => {
        return d.sectionName === 'flights'
    })
    const { data: { flights = [] } = {} } = flightData || {}
    const nextFlights = flights
        .filter(
            ({ flightDate }: { flightDate: string }) =>
                new Date(flightDate).getTime() > new EventrDate().getUnixMs
        )
        .sort(
            (a: any, b: any) =>
                new Date(a.departure?.dep_estimated).getTime() -
                new Date(b.departure?.dep_estimated).getTime()
        )
    return nextFlights
}

export const getNextActivityData = (data: any) => {
    const activityData = data?.v2?.find((a: any) => {
        return a.sectionName === 'activities'
    })
    const { data: { eventCalendar = [] } = {} } = activityData || {}
    const nextActivities = eventCalendar
        ?.filter(({ date }: { date: string }) => {
            return (
                new Date(addOneDayIfDateType(date)).getTime() >=
                new EventrDate().getUnixMs
            )
        })

        .sort(
            (a: any, b: any) =>
                new Date(a?.date).getTime() - new Date(b?.date).getTime()
        )
    return nextActivities
}

export const getNameFromUser = (user: AppUser) => {
    if (!user.firstName) {
        return user.email
    }
    return getUserName(user.firstName, user.lastName)
}

export const splitValueID = (valueAndID: string) => {
    if (!valueAndID) return { value: '', ID: '' }
    const stringValue = valueAndID.toString()
    const split = stringValue.split(ID_PREFIX)
    return { value: split[0], ID: split[1] }
}

export const getValue = (valueAndID: string) => {
    const { value } = splitValueID(valueAndID)
    return value
}
export const capitalizeFirstLetter = (val: string) => {
    return val.charAt(0).toUpperCase() + val.slice(1)
}

export const getRandomString = (length: number) => {
    var randomChars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    var result = ''
    for (var i = 0; i < length; i++) {
        result += randomChars.charAt(
            Math.floor(Math.random() * randomChars.length)
        )
    }
    return result
}

export const bytesToBase64 = (bytes: Uint8Array) => {
    const binString = Array.from(bytes, (x) => String.fromCodePoint(x)).join('')
    return btoa(binString)
}

export const getRocketChatWebsocket = (org: string) => {
    const stage = reactAppStage()
    if (stage === 'demo')
        return `wss://${org}.${ROCKETCHAT_DEMO_DOMAIN}/websocket`

    if (stage === 'test')
        return `wss://${org}.${ROCKETCHAT_TEST_DOMAIN}/websocket`

    if (stage === 'dev')
        return `wss://${org}.${ROCKETCHAT_DEV_DOMAIN}/websocket`

    if (stage === 'local') return `ws://chat-${org}.localhost:3500/websocket`

    return `wss://${org}.${ROCKETCHAT_DEV_DOMAIN}/websocket`
}

export const getRocketChatUrl = (org: string) => {
    const stage = reactAppStage()
    if (stage === 'demo') return `https://${org}.${ROCKETCHAT_DEMO_DOMAIN}`

    if (stage === 'test') return `https://${org}.${ROCKETCHAT_TEST_DOMAIN}`
    if (stage === 'dev') return `https://${org}.${ROCKETCHAT_DEV_DOMAIN}`

    if (stage === 'local') return `http://chat-${org}.localhost:3500`

    return `https://${org}.${ROCKETCHAT_DEMO_DOMAIN}`
}

export const getRedirectUrl = (url: string) => {
    const stage = reactAppStage()
    switch (stage) {
        case 'demo':
            return `${APPLINKS_DEMO_URL}/${url}`
        case 'test':
            return `${APPLINKS_TEST_URL}/${url}`
        case 'dev':
            return `${APPLINKS_DEV_URL}/${url}`
        case 'local':
            return window.location.origin + '/' + url
        default:
            return `${APPLINKS_DEMO_URL}/${url}`
    }
}

export const getSubdomain = () => {
    const stage = reactAppStage()
    switch (stage) {
        case 'demo':
            return `${DEMO_SUBDOMAIN}`
        case 'test':
            return `${TEST_SUBDOMAIN}`
        case 'dev':
            return `${DEV_SUBDOMAIN}`
        case 'local':
            return ``
        default:
            return `${DEMO_SUBDOMAIN}`
    }
}

export const sectionInfoToEmptyV2 = (sectionInfo: ISection): V2 => {
    return {
        data: {},
        sectionName: sectionInfo?.name,
        renderer: sectionInfo?.properties?.renderer as any,
        sectionID: sectionInfo?.id,
        rendererOptions: sectionInfo?.properties?.rendererOptions as any,
        sectionDisplayName: sectionInfo?.properties?.sectionDisplayName ?? '',
        sectionDisplayNameTeams:
            sectionInfo?.properties?.sectionDisplayNameTeams ?? '',
        RCChannels: [],
    }
}

export const getNotParsedUrlParam = (searchKey: string) => {
    const queryParameters = window.location.search.substring(1)
    if (!queryParameters) {
        return ''
    }
    let found = ''
    queryParameters.split('&').forEach((param) => {
        const [key, value] = param.split('=')

        if (key === searchKey) {
            found = value
        }
    })

    return found
}

//Updates local version of itinerary with new section item data
// it used to have an updated version of the itinerary before useEffect kicks in to update from the server
export const updateSection = (
    itinData: IItinerary,
    sectionItem: any,
    sectionId: number
) => {
    const newItinData = { ...itinData }
    const sectionIndex = newItinData.v2?.findIndex(
        (section) => section.sectionID === sectionId
    )
    if (sectionIndex === -1) {
        return newItinData
    }
    const keys = Object.keys(newItinData.v2?.[sectionIndex]?.data || {})
    const firstKey = keys.length > 0 ? keys[0] : undefined
    const found = (newItinData.v2[sectionIndex].data[firstKey] as []).find(
        (item: any, index: number) => {
            if (item._id === sectionItem._id) {
                newItinData.v2[sectionIndex].data[firstKey][index] = sectionItem
                return true
            }
            return false
        }
    )
    if (!found) {
        newItinData.v2[sectionIndex].data[firstKey].push(sectionItem)
    }
    return newItinData
}

/**
 * Filter out pending items. (for use within filter func)
 */
export function filterOutPendingTag(item: any): boolean {
    if (!item) {
        /// Error?
        return false
    }

    if (item._pending) {
        return false
    } else {
        return true
    }
}
/**
 * Filter out items that are copied.
 */
export function filterOutCopiedItems(item: any): boolean {
    if (item._og_code) {
        return false
    }
    return true
}
export const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms))

export const Utils = {
    getToken: () => {
        return bs().IDtoken
    },

    setToken: async (accessToken: string) => {
        localStorage.setItem('accessToken', accessToken)
        Preferences.set({ key: 'accessToken', value: accessToken })
    },

    setRefreshToken: async (refreshToken: string) => {
        localStorage.setItem('refreshToken', refreshToken)
        Preferences.set({ key: 'refreshToken', value: refreshToken })
    },

    getVersion: async () => {
        if (!Capacitor.isNativePlatform()) return 'webView'
        console.log(await App.getInfo())

        return (await App.getInfo()).version
    },

    logoutClient: async () => {
        return await Preferences.clear()
    },

    hapticFeedback: async (
        feedbackType:
            | 'light'
            | 'medium'
            | 'heavy'
            | 'selectionClick'
            | 'vibrate'
    ) => {
        if (!Capacitor.isNativePlatform()) return
        const feedbackmap = {
            light: ImpactStyle.Light,
            medium: ImpactStyle.Medium,
            heavy: ImpactStyle.Heavy,
        }
        if (feedbackType === 'selectionClick')
            return await Haptics.selectionStart()
        if (feedbackType === 'vibrate') return await Haptics.vibrate()
        return await Haptics.impact({
            style: feedbackmap[feedbackType],
        })
    },

    pushLog: async (log: FlutterLog) => {
        logsSignal.value = [...logsSignal.peek(), log]
    },

    downloadAndOpenFile: async (url: any, filename?: string) => {
        await requestFilePermission()
        if (isConnectivityAvailable.peek()) {
            try {
                loadingSignal.value = true
                const cachefile = await CacheFile(url, true)
                const file = await moveFromCacheToDocuments(cachefile.path)
                loadingSignal.value = false
                if (Capacitor.isNativePlatform()) {
                    FileOpener.open({ filePath: file?.uri ?? '' })
                } else {
                    return (window.location.href = URL.createObjectURL(
                        cachefile.blob as Blob
                    ))
                }
            } catch (error) {
                spErrorTags('downloadAndOpenFile', ['FILES'], error)
                loadingSignal.value = false
            }
        } else {
            const cacheFile = await getFileFromCache(url)
            const file = await moveFromCacheToDocuments(cacheFile.path)
            if (Capacitor.isNativePlatform()) {
                FileOpener.open({ filePath: file?.uri ?? '' })
            } else {
                return (window.location.href = URL.createObjectURL(
                    cacheFile.blob as Blob
                ))
            }
        }
    },

    getReactVersion: async () => {
        return import.meta.env.VITE_APP_VERSION
    },
}

interface ICacheFileResult {
    path: string
    blob: Blob | string
}
/**
 * Downloads a file from the specified URL and saves it in the cache directory.
 *
 * @param url - The URL of the file to be downloaded.
 * @returns A promise that resolves to the downloaded file.
 */
export const CacheFile = async (
    url: string,
    updateCache?: boolean
): Promise<ICacheFileResult> => {
    if (!url) {
        return { path: '', blob: '' }
    }
    const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, '')
    if (updateCache) {
        const file = await downloadToCache(url)
        return { path: filePath, blob: file?.blob || '' }
    }
    // check if file is already in cache
    try {
        const cacheImage = await Filesystem.readFile({
            path: filePath,
            directory: Directory.Cache,
        })
        if (cacheImage.data) {
            return { path: filePath, blob: cacheImage.data }
        }
    } catch (error) {
        // file not in cache
        const file = await downloadToCache(url)
        if (file) {
            return { path: filePath, blob: file?.blob || '' }
        }
    }
    // Ensure a default return in case all other paths fail
    return { path: '', blob: '' }
}

const downloadToCache = async (url: string) => {
    const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, '')
    // recursive flag does not work with android  need to check if directroy exists and create it

    if (Capacitor.isNativePlatform()) {
        const fileDir = filePath.split('/').slice(0, -1).join('/')
        await createDirectoryIfNotExists(fileDir)
    }
    try {
        const file = await Filesystem.downloadFile({
            url,
            directory: Directory.Cache,
            headers: {
                Authorization: `Bearer ${await Utils.getToken()}`,
                'x-sp-guest-jwt': getGuestJWT() ?? '',
            },
            path: filePath,
            recursive: true,
        })
        return file
    } catch (error) {
        spErrorTags('downloadToCache', ['FILES'], error)
    }
}

const getGuestJWT = () => {
    if (useGuestStore.getState().isGuestMode) {
        return useGuestStore.getState().guestData?.guest_token
    }
    return null
}

export const moveFromCacheToDocuments = async (path: string) => {
    try {
        const cacheImage = await Filesystem.readFile({
            path: path,
            directory: Directory.Cache,
        })
        if (cacheImage.data) {
            const fileName = path.split('/').pop()
            return Filesystem.writeFile({
                path: fileName,
                data: cacheImage.data,
                directory: Directory.Documents,
            })
        }
    } catch (error) {
        spErrorTags('moveFromCacheToDocuments', ['FILES'], error)
    }
}

export const getFileFromCache = async (
    url: string
): Promise<ICacheFileResult> => {
    const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, '')
    try {
        const cacheImage = await Filesystem.readFile({
            path: filePath,
            directory: Directory.Cache,
        })
        if (cacheImage.data) {
            return { path: filePath, blob: cacheImage.data }
        }
    } catch (error) {
        spErrorTags('getFileFromCache', ['FILES'], error)
    }
}

export const requestFilePermission = async () => {
    try {
        if (!Capacitor.isNativePlatform()) return
        const currentPerm = await Filesystem.checkPermissions()
        if (currentPerm.publicStorage === 'granted') return currentPerm
        const permission = await Filesystem.requestPermissions()
        return permission
    } catch (error) {
        spErrorTags('requestFilePermission', ['FILES'], error)
    }
}

export const createDirectoryIfNotExists = async (path: string) => {
    try {
        await Filesystem.readdir({
            path,
            directory: Directory.Cache,
        })
    } catch (error) {
        try {
            await Filesystem.mkdir({
                path,
                directory: Directory.Cache,
                recursive: true, // This will create directories along the path if they do not exist
            })
        } catch (error) {
            spErrorTags('createDirectoryIfNotExists', ['FILES'], error)
        }
    }
}

export const enableChat = () => {
    if (!checkClaimsWeb(['/'], ['chatAccess'])) {
        return false
    }
    try {
        if (!bs().orgConfig?.disableChat) {
            /// Will be true if to disable
            return true
        } else {
            /// if false or undifined
            return false
        }
    } catch {
        /// Default on.
        return true
    }
}

export const getTimezoneOfItinerary = (itinData: IItinerary) => {
    try {
        let timezone = itinData?.v2?.[0]?.data?.OSMAddress?.timeZone

        /// to deal with legacy cc set
        if (!timezone && itinData?.v2?.[0]?.data?.OSMAddress?.timeZone) {
            const selectedCountry = CountryCodesWithNames.find((countryObj) => {
                return (
                    countryObj.id ===
                    itinData?.v2?.[0]?.data?.OSMAddress?.timeZone
                )
            })
            if (selectedCountry) {
                timezone = selectedCountry.timezones[0] /// take first
            }
        }

        return timezone
    } catch {
        return undefined
    }
}

export function mapRoleNamesWithRole(roleName: string, roleMap: TOrgRole[]) {
    if (!roleMap) return roleName
    const roleMapSingle = roleMap.find((roleObj) => {
        return roleObj.role === roleName
    })
    if (roleMapSingle) {
        return roleMapSingle.name
    } else {
        return roleName
    }
}

/**
 * Copies the provided content to the clipboard.
 *
 * @param contentConst - The content to be copied to the clipboard.
 * @param version - An object containing the versions of Flutter and React used. This is obtained from useAuthContext().
 * @param snackBar - An instance of the snackbar hook.
 *
 * @returns void
 *
 * @async
 */
export async function copyToClipboard(
    contentConst: string,
    version: { buildVersion?: string; reactVersion?: string } //version is taken from useAuthContext()
) {
    // Check if the content is not empty
    if (contentConst && contentConst !== '') {
        try {
            // Check if the platform is native
            if (Capacitor.isNativePlatform()) {
                // Check if the Flutter version is greater than or equal to 0.9.24
                if (compareBuildVersion(version.buildVersion ?? '', '0.9.24')) {
                    // Write the content to the clipboard
                    Clipboard.write({
                        string: String(contentConst),
                    })
                    // If the platform is iOS, show a success snackbar
                    if (Capacitor.getPlatform() === 'ios') {
                        defaultAddSnack(
                            i18n.t('snack.copied-clipboard'),
                            'success'
                        )
                    }
                } else {
                    return
                }
            } else {
                // If the platform is not native
                if (navigator.clipboard) {
                    // Write the content to the clipboard
                    await navigator.clipboard.writeText(String(contentConst))
                    // Show a success snackbar
                    defaultAddSnack(i18n.t('snack.copied-clipboard'), 'success')
                } else {
                    // If the clipboard is not available, show an error snackbar
                    defaultAddSnack(i18n.t('snack.not-secure-context'), 'error')
                }
            }
        } catch (error) {
            spError(error)
        }
    }
}

/**
 * Converts a data URL to a File object.
 *
 * @param {string} dataurl - The data URL to convert.
 * @param {string} filename - The name of the file.
 * @returns {File} The converted File object.
 */
export function dataURLtoFile(dataurl: string, filename: string) {
    var arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)?.[1] || '',
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
}

/**
 * i needed a quick function to get all day of a datestring, eg set it to the end or start of day.
 */
export function getAllDayOfDate(
    dateTimeString: string,
    toggle: 'start' | 'end'
) {
    const newEventrDate = new EventrDate(dateTimeString)
    switch (toggle) {
        case 'start':
            newEventrDate.setFromObject({ hour: 0, minute: 0, millisecond: 1 })
            return newEventrDate.dateTimeString
        case 'end':
            newEventrDate.setFromObject({
                hour: 23,
                minute: 59,
                millisecond: 999,
            })
            return newEventrDate.dateTimeString
    }
}

/**
 * Returns react branch version.
 */
export const reactBranch = () => {
    if (import.meta.env.VITE_APP_VERSION) {
        return import.meta.env.VITE_APP_VERSION.split('-')[1]
    } else {
        /// fallback is react app stage.
        return reactAppStage() ?? 'demo'
    }
}

/**
 * Colour for dev Overlay for things...
 * from local storage
 */
export const reactAppStageColour = () => {
    if (!localStorage.getItem('REACT_APP_STAGE')) {
        return 'red'
    }
    return getColourFromStage(reactAppStage())
}

/**
 * Colour for dev Overlay for things...
 * from local storage
 */
export const reactAppBranchColour = () => {
    if (!reactBranch()) {
        return 'red'
    }
    return getColourFromStage(reactBranch())
}
export const isWebPreview = import.meta.env.VITE_APP_WEB === 'true'
const getColourFromStage = (stage: string) => {
    switch (stage) {
        case 'dev':
            return '#18B1AB'
        case 'test':
            return '#F3C724'
        case 'prod':
            return '#4F3473'
        case 'local':
            return '#4F3473'
        case 'capacitor':
            return '#4F3473'
        default:
            return 'white'
    }
}

export const isDebugUser = () => {
    if (isWebPreview) return false
    if (isDebugModeEnabled(bs()?.profile?.debugFlag, 1)) {
        return true
    }
    return false
}
export const captureSentryMessage = (message: string) => {
    if (isWebPreview) return
    if (isDebugModeEnabled(bs().profile?.debugFlag, 1)) {
        Sentry.captureMessage(message, {
            fingerprint: [bs().profile?.email ?? 'NO_USER'],
        })
    }
}

export const isDebugModeEnabled = (
    debugFlag: number | undefined,
    flagType: number
) => {
    if (debugFlag === undefined) {
        return false
    }

    return (debugFlag & flagType) !== 0
}

/**
 * device detect for the name of device pref. needs the func passed in as meant to be ran within a comp...
 */
export function getDevicePrefName(
    deviceDetect: (userAgent: string | undefined) => any
): string {
    const deviceInfo = deviceDetect(navigator.userAgent)
    const vendor = deviceInfo.vendor || 'WebView'
    const model = deviceInfo.model || 'WebView'

    return `${vendor}-${model}`
}

export const openBrowser = (url: string) => {
    let tmpUrl = url
    if (!url.includes('https://') && !url.includes('http://')) {
        tmpUrl = 'https://' + tmpUrl
    }
    if (Capacitor.isNativePlatform()) {
        InAppBrowser.openInExternalBrowser({
            url: tmpUrl,
        })
    } else {
        window.open(tmpUrl)
    }
}

export function openMobileMapsLink(url: string) {
    if (Capacitor.isNativePlatform()) {
        if (Capacitor.getPlatform() === 'ios') {
            /// For IOS
            const mapsLinkIOS = 'maps://?q='
            window.open(mapsLinkIOS + encodeURI(url), '_system')
        } else {
            /// For Android
            const mapsLinkAndroid = 'geo:0,0?q='
            window.open(mapsLinkAndroid + encodeURI(url), '_system')
        }
    } else {
        /// For WEB
        window.open('maps://?q=' + encodeURI(url))
    }
}

export function getMapsUrlFromLngLat(q: LngLat | string) {
    let url: string
    if (typeof q === 'string') {
        url = q
    } else {
        url = `${q.lng},${q.lat}`
    }

    if (Capacitor.isNativePlatform()) {
        if (Capacitor.getPlatform() === 'ios') {
            /// For IOS
            const mapsLinkIOS = 'maps://?q='
            return mapsLinkIOS + encodeURI(url)
        } else {
            /// For Android
            const mapsLinkAndroid = 'geo:0,0?q='
            return mapsLinkAndroid + encodeURI(url)
        }
    } else {
        /// For WEB
        return 'maps://?q=' + encodeURI(url)
    }
}

export const getMinutesFromUnixTimestamp = (timestamp: number): number => {
    return Math.floor(timestamp / 60000)
}

export const getDelayPromise = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(true)
        }, delay)
    })
}

export const handleOrgChangeNotification = async (org: UserOrganisation) => {
    setDevicePref('theme', '', getDevicePrefName(deviceDetect))
    useThemeContext.getState().setOverride(false)
    useGuestStore.setState(useGuestStore.getInitialState())
    await bs().setSelectedOrg(org)
}

export const cleanRCName = (name: string) => {
    return name
        .replace(/#/g, '')
        .replace(/_SPRC_.*$/, '')
        .replace(/_/g, ' ')
}
